import * as React from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { DropdownInput } from "../DropdownInput";
import { TextInput } from "../TextInput";

const TELEMETRY_OPTIONS = [
  { value: "eld_only", label: "ELD Only" },
  { value: "dash_cam_dual_ai_connected", label: "Dash Camera" },
];

const arraysEqual = (a = [], b = []) =>
  a.length === b.length && a.every((v, i) => v === b[i]);

export function DataSharing() {
  const { control, register, setValue } = useFormContext();

  // Source-of-truth carriers
  const step1Carrier = useWatch({ control, name: "step1.carrier" });

  // Mirror into step4.carrier so Step4Schema can validate itself
  const step4Carrier = useWatch({ control, name: "step4.carrier" });

  React.useEffect(() => {
    register("step4.carrier");
  }, [register]);

  React.useEffect(() => {
    const list = Array.isArray(step1Carrier)
      ? step1Carrier
      : step1Carrier
      ? [step1Carrier]
      : [];
    const normalized = list.map((s) => (s ?? ""));

    if (!arraysEqual(normalized, step4Carrier ?? [])) {
      setValue("step4.carrier", normalized, {
        shouldDirty: false,
        shouldValidate: true,
      });
    }
  }, [step1Carrier, step4Carrier, setValue]);

  // Drive UI from mirrored carriers
  const carriers = (step4Carrier ?? []).map((s) => (s ?? ""));
  const hasCW = carriers.includes("Cover Whale");
  const hasNir = carriers.includes("Nirvana");
  const cwAndNirvana = hasCW && hasNir;
  const cwOnly = hasCW && !hasNir;
  const nirOnly = hasNir && !hasCW;

  // Legacy Nirvana-only dropdown + provider input
  const renderNirvanaOnly = () => (
    <div className="space-y-6">
      <div className="rounded-xl">
        <div className="flex gap-6 md:items-start">
          <div className="space-y-4">
            <h2 className="text-2xl md:text-3xl font-semibold text-slate-900">
              Choosing Telemetry Data Sharing Method
            </h2>
            <p className="text-sm text-slate-700">
              To enhance driving safety and affordability, Nirvana quotes require an ELD or Dash Camera data sharing method.
            </p>
          </div>
        </div>

        <div className="space-y-4 mt-4">
          <div className="space-y-2">
            <DropdownInput
              control={control}
              label="Data Sharing Option"
              dropdownLabel="Data Sharing Option"
              stepName="step4.telemetryOption"
              stepId="step4"
              itemList={TELEMETRY_OPTIONS}
            />
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <TextInput
          register={register}
          label="Who is the insured ELD/Dash Camera Provider?"
          id="telemetry-provider"
          stepName="step4.telemetryProvider"
          stepId="step4"
        />
      </div>
    </div>
  );

  // Cover Whale-only flow: just the data sharing option (from Nirvana)
  const renderCoverWhaleOnly = () => (
    <div className="space-y-6">
      <div className="rounded-xl">
        <div className="flex gap-6 md:items-start">
          <div className="space-y-4">
            <h2 className="text-2xl md:text-3xl font-semibold text-slate-900">
              Cover Whale Data Sharing
            </h2>
            <p className="text-sm text-slate-700">
              Cover Whale Auto Liability policies mandate telemetry data sharing. Please answer the following about the insured&apos;s current setup.
            </p>
          </div>
        </div>

        <div className="space-y-4 mt-4">
          <div className="space-y-2">
            <DropdownInput
              control={control}
              label="Data Sharing Option"
              dropdownLabel="Data Sharing Option"
              stepName="step4.telemetryOption"
              stepId="step4"
              itemList={TELEMETRY_OPTIONS}
            />
          </div>
        </div>
      </div>
    </div>
  );

  // Cover Whale + Nirvana combined flow: mirror Nirvana-only flow
  const renderCoverWhaleAndNirvana = () => (
    <div className="space-y-6">
      <div className="rounded-xl space-y-6">
        <div className="space-y-4">
          <h2 className="text-2xl md:text-3xl font-semibold text-slate-900">
            Telemetry Data Sharing – ELD and Dash Camera
          </h2>
          <p className="text-sm text-slate-700">
            To enhance driving safety and affordability, Nirvana and Cover Whale quotes require a data sharing method. Please answer the following about the insured&apos;s current setup.
          </p>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <DropdownInput
              control={control}
              label="Data Sharing Option"
              dropdownLabel="Data Sharing Option"
              stepName="step4.telemetryOption"
              stepId="step4"
              itemList={TELEMETRY_OPTIONS}
            />
          </div>

          <div className="space-y-2">
            <TextInput
              register={register}
              label="Who is the insured ELD/Dash Camera Provider?"
              id="telemetry-provider"
              stepName="step4.telemetryProvider"
              stepId="step4"
            />
          </div>
        </div>
      </div>
    </div>
  );

  if (nirOnly) {
    return renderNirvanaOnly();
  }

  if (cwOnly) {
    return renderCoverWhaleOnly();
  }

  if (cwAndNirvana) {
    return renderCoverWhaleAndNirvana();
  }

  return null;
}

